import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Loader2, CheckCircle2, AlertCircle, Calendar,
  Clock, User, Wrench,
} from 'lucide-react';

import { Button, Card } from '../../ui-v2';
import { getMiCampana, getTecnicosDisponibles, getSlots, reservarCita } from '../api';
import CalendarioReserva from '../components/CalendarioReserva';
import CondicionesCancelacion from '../compartidos/CondicionesCancelacion';

/**
 * Flujo de reserva.
 *   Modo 'libre': paso 1 elegir técnico → paso 2 calendario → paso 3 hora.
 *   Modo 'automatica': paso 1 calendario → paso 2 hora (backend asigna técnico).
 */
export default function ReservarCita() {
  const navigate = useNavigate();
  const { id: idParam } = useParams();
  const id = parseInt(idParam, 10);

  const [campana, setCampana] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [cargaError, setCargaError] = useState(null);

  const [tecnicoId, setTecnicoId] = useState(null);
  const [tecnicos, setTecnicos] = useState([]);
  const [cargandoTecnicos, setCargandoTecnicos] = useState(false);

  const [slots, setSlots] = useState([]);
  const [cargandoSlots, setCargandoSlots] = useState(false);
  const [errorSlots, setErrorSlots] = useState(null);

  const [fechaSel, setFechaSel] = useState(null);
  const [horaSel, setHoraSel] = useState(null);

  const [reservando, setReservando] = useState(false);
  const [errorReserva, setErrorReserva] = useState(null);
  const [reservaOk, setReservaOk] = useState(null);

  useEffect(() => {
    if (!id) return;
    setCargando(true);
    getMiCampana(id)
      .then((c) => {
        if (c.estado !== 'Activa') {
          setCargaError('Esta campaña no está activa actualmente.');
          return;
        }
        setCampana(c);
      })
      .catch((e) => {
        console.error('[ReservarCita]', e);
        setCargaError(
          e?.response?.status === 403
            ? 'No estás incluido en esta campaña.'
            : 'No se pudo cargar la campaña.'
        );
      })
      .finally(() => setCargando(false));
  }, [id]);

  useEffect(() => {
    if (!campana || campana.asignacion_modo !== 'libre') return;
    setCargandoTecnicos(true);
    getTecnicosDisponibles(id)
      .then((res) => {
        setTecnicos(res.data || []);
        if (res.data?.length === 1) setTecnicoId(res.data[0].id);
      })
      .catch((e) => console.error('[tecnicos]', e))
      .finally(() => setCargandoTecnicos(false));
  }, [campana, id]);

  const cargarSlots = useCallback(async () => {
    if (!campana) return;
    if (campana.asignacion_modo === 'libre' && !tecnicoId) return;

    setCargandoSlots(true);
    setErrorSlots(null);
    try {
      const params = {};
      if (tecnicoId) params.tecnico_id = tecnicoId;
      const res = await getSlots(id, params);
      setSlots(res.slots || []);
      setFechaSel(null);
      setHoraSel(null);
    } catch (e) {
      console.error('[slots]', e);
      setErrorSlots(e?.response?.data?.error || 'No se pudieron cargar los horarios.');
    } finally {
      setCargandoSlots(false);
    }
  }, [campana, id, tecnicoId]);

  useEffect(() => {
    cargarSlots();
  }, [cargarSlots]);

  async function confirmar() {
    if (!fechaSel || !horaSel) return;
    setReservando(true);
    setErrorReserva(null);
    try {
      const payload = { fecha: fechaSel, hora_inicio: horaSel };
      if (campana.asignacion_modo === 'libre') payload.tecnico_id = tecnicoId;
      const res = await reservarCita(id, payload);
      setReservaOk(res);
    } catch (e) {
      console.error('[reservar]', e);
      const code = e?.response?.data?.code;
      const msg = e?.response?.data?.error || 'No se pudo completar la reserva.';
      setErrorReserva(msg);
      if (code === 'slot_ocupado' || code === 'slot_invalido') {
        await cargarSlots();
      }
    } finally {
      setReservando(false);
    }
  }

  if (cargando) {
    return (
      <div className="px-6 py-12 text-center text-slate-500 text-sm">
        <Loader2 className="w-5 h-5 inline-block animate-spin mr-2" />
        Cargando…
      </div>
    );
  }

  if (cargaError || !campana) {
    return (
      <div className="px-6 py-12 max-w-xl mx-auto">
        <div className="border border-red-200 bg-red-50 rounded-lg p-4 text-red-700 text-sm">
          {cargaError || 'No se pudo cargar la campaña.'}
        </div>
        <div className="mt-4">
          <Button variant="outline" onClick={() => navigate('/citas-tecnicas/mis-campanas')}>
            <ArrowLeft className="w-4 h-4" />
            Volver
          </Button>
        </div>
      </div>
    );
  }

  if (reservaOk) {
    return (
      <PantallaExito
        campana={campana}
        cita={reservaOk}
        onVolver={() => navigate('/citas-tecnicas/mis-campanas')}
      />
    );
  }

  const modoLibre = campana.asignacion_modo === 'libre';
  const horasDelDia = fechaSel
    ? slots.filter((s) => s.fecha === fechaSel).map((s) => ({
        hora_inicio: s.hora_inicio,
        hora_fin: s.hora_fin,
        tecnico_id: s.tecnico_id,
      }))
    : [];

  return (
    <div className="px-6 py-5 min-h-full max-w-3xl">
      <button
        type="button"
        onClick={() => navigate('/citas-tecnicas/mis-campanas')}
        className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-2"
      >
        <ArrowLeft className="w-3 h-3" /> Volver a Mis Citas
      </button>
      <h1 className="text-xl font-semibold text-slate-900">Reservar tu cita</h1>
      <p className="text-sm text-slate-500 mt-1 mb-5">
        <strong>{campana.titulo}</strong> · citas de {campana.duracion_cita} min entre {campana.horario_inicio} y {campana.horario_fin}
      </p>

      {modoLibre && (
        <Seccion numero={1} titulo="Elige técnico" activo={true} completo={!!tecnicoId}>
          {cargandoTecnicos ? (
            <div className="text-sm text-slate-500">
              <Loader2 className="w-4 h-4 inline-block animate-spin mr-2" />
              Cargando…
            </div>
          ) : tecnicos.length === 0 ? (
            <p className="text-sm text-slate-500">No hay técnicos asignados a esta campaña.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {tecnicos.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTecnicoId(t.id)}
                  className={
                    'text-left border rounded-lg p-3 flex items-center gap-3 transition-colors ' +
                    (tecnicoId === t.id
                      ? 'border-slate-900 bg-slate-900 text-white'
                      : 'border-slate-200 bg-white hover:border-slate-400')
                  }
                >
                  <User className={'w-4 h-4 ' + (tecnicoId === t.id ? 'text-white' : 'text-slate-400')} />
                  <span className="text-sm font-medium">{t.nombre_completo}</span>
                </button>
              ))}
            </div>
          )}
        </Seccion>
      )}

      <Seccion
        numero={modoLibre ? 2 : 1}
        titulo="Elige día"
        activo={!modoLibre || !!tecnicoId}
        completo={!!fechaSel}
      >
        {(!modoLibre || tecnicoId) && (
          <>
            {cargandoSlots ? (
              <div className="text-sm text-slate-500">
                <Loader2 className="w-4 h-4 inline-block animate-spin mr-2" />
                Calculando horarios disponibles…
              </div>
            ) : errorSlots ? (
              <div className="text-sm text-red-600">{errorSlots}</div>
            ) : slots.length === 0 ? (
              <div className="text-sm text-slate-500 bg-slate-50 border border-slate-100 rounded-md px-3 py-2">
                No hay horarios disponibles para los técnicos seleccionados.
              </div>
            ) : (
              <CalendarioReserva
                slots={slots}
                fechaInicio={campana.fecha_inicio}
                fechaLimite={campana.fecha_limite}
                fechaSeleccionada={fechaSel}
                onSeleccionarFecha={(f) => { setFechaSel(f); setHoraSel(null); }}
              />
            )}
          </>
        )}
      </Seccion>

      <Seccion
        numero={modoLibre ? 3 : 2}
        titulo="Elige hora"
        activo={!!fechaSel}
        completo={!!horaSel}
      >
        {fechaSel && (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
            {horasDelDia.map((h) => (
              <button
                key={h.hora_inicio}
                type="button"
                onClick={() => setHoraSel(h.hora_inicio)}
                className={
                  'border rounded-lg py-2 text-sm font-medium transition-colors ' +
                  (horaSel === h.hora_inicio
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400')
                }
              >
                {h.hora_inicio}
              </button>
            ))}
          </div>
        )}
      </Seccion>

      {horaSel && (
        <Card className="p-4 mt-4 border-emerald-200 bg-emerald-50">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-sm font-medium text-emerald-900">Tu cita</div>
              <p className="text-sm text-emerald-800 mt-1">
                {formatFechaLarga(fechaSel)} · <strong>{horaSel}</strong>
                {!modoLibre && (
                  <span className="text-xs text-emerald-700"> · técnico asignado automáticamente</span>
                )}
              </p>
              {modoLibre && (
                <p className="text-xs text-emerald-700 mt-0.5">
                  Con {tecnicos.find((t) => t.id === tecnicoId)?.nombre_completo}
                </p>
              )}
            </div>
            <Button onClick={confirmar} disabled={reservando}>
              {reservando ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Confirmar reserva
            </Button>
          </div>

          {errorReserva && (
            <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-100 rounded-md p-2.5 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              {errorReserva}
            </div>
          )}

          {/* Fase 14b · #12 (MODO A) — condiciones antes de confirmar la reserva */}
          <div className="mt-3">
            <CondicionesCancelacion
              fechaReserva={fechaSel}
              horaReserva={horaSel}
              horasAntelacionCancelar={campana.horas_antelacion_cancelar || 0}
              cancelacionesMax={campana.cancelaciones_max ?? campana.max_cancelaciones_default ?? 0}
              cancelacionesUsadas={campana.cancelaciones_usadas ?? 0}
            />
          </div>
        </Card>
      )}
    </div>
  );
}

function Seccion({ numero, titulo, activo, completo, children }) {
  return (
    <Card
      className={
        'p-4 mb-3 ' +
        (!activo ? 'opacity-50 pointer-events-none ' : '') +
        (completo ? 'border-emerald-200' : '')
      }
    >
      <div className="flex items-center gap-2 mb-3">
        <span
          className={
            'w-6 h-6 rounded-full grid place-items-center text-xs font-semibold ' +
            (completo
              ? 'bg-emerald-500 text-white'
              : activo
              ? 'bg-slate-900 text-white'
              : 'bg-slate-200 text-slate-500')
          }
        >
          {completo ? <CheckCircle2 className="w-4 h-4" /> : numero}
        </span>
        <h2 className="text-sm font-semibold text-slate-800">{titulo}</h2>
      </div>
      {children}
    </Card>
  );
}

function PantallaExito({ campana, cita, onVolver }) {
  return (
    <div className="px-6 py-12 max-w-xl mx-auto text-center">
      <div className="w-14 h-14 rounded-full bg-emerald-100 grid place-items-center mx-auto mb-4">
        <CheckCircle2 className="w-7 h-7 text-emerald-600" />
      </div>
      <h1 className="text-xl font-semibold text-slate-900 mb-2">Cita reservada</h1>
      <p className="text-sm text-slate-600 mb-5">
        Tu cita para <strong>{campana.titulo}</strong> está confirmada.
      </p>

      <Card className="p-4 text-left mb-5">
        <p className="text-sm">
          <Calendar className="w-4 h-4 inline-block mr-2 text-slate-400" />
          {formatFechaLarga(cita.fecha)}
        </p>
        <p className="text-sm mt-1">
          <Clock className="w-4 h-4 inline-block mr-2 text-slate-400" />
          {cita.hora_inicio}–{cita.hora_fin}
        </p>
        {cita.tecnico_nombre && (
          <p className="text-sm mt-1">
            <Wrench className="w-4 h-4 inline-block mr-2 text-slate-400" />
            {cita.tecnico_nombre}
          </p>
        )}
      </Card>

      <Button onClick={onVolver}>
        Volver a Mis Citas
      </Button>
    </div>
  );
}

function formatFechaLarga(s) {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('es-ES', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
}
